import { useEffect, useRef } from 'react';
import { factionOf } from '@bm/shared';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { useElapsedMs } from '@/lib/clock';
import { haptics } from '@/lib/haptics';
import { AppShell } from '@/components/AppShell';
import { GameHeader } from '@/components/GameHeader';
import { TallyBar } from '@/components/TallyBar';

/**
 * Dramaturgie der Auszählung (GDD §37) als Zeitachse über `phaseStartedAt`.
 *
 * Serverzeit statt einer eigenen Timer-Kette: Wer mitten in der Phase neu
 * verbindet oder das Handy erst später wieder entsperrt, steigt an der
 * richtigen Stelle ein, statt die Inszenierung von vorn zu sehen.
 * `ROUND_RESULT_SECONDS` ist 8 — die Beats brauchen davon knapp die Hälfte.
 *
 * Sichtbarkeit hängt ausschließlich an dieser Zeitachse, nie an einer
 * laufenden Animation: die Animationen schmücken die Beats, sie schalten sie
 * nicht.
 */
const COUNTING_BEAT_MS = 1200;
/** Versatz zwischen zwei Balken. */
const BAR_STAGGER_MS = 150;
/** Pause zwischen dem letzten Balken und der Auflösung. */
const REVEAL_DELAY_MS = 600;

export function RoundResultScreen() {
  const view = useAppStore((s) => s.view);
  const result = view?.lastResult ?? null;
  const entry = view?.history.at(-1);

  const barCount = entry?.tally.length ?? 1;
  const highlightAt = COUNTING_BEAT_MS + barCount * BAR_STAGGER_MS;
  const revealAt = highlightAt + REVEAL_DELAY_MS;
  const elapsed = useElapsedMs(view?.phaseStartedAt ?? 0, revealAt);

  const beatDone = useRef(false);
  useEffect(() => {
    if (beatDone.current || elapsed < highlightAt) return;
    beatDone.current = true;
    haptics.tick();
  }, [elapsed, highlightAt]);

  // Unentschieden hat einen eigenen Screen; alles andere landet hier.
  if (!view || !result || result.type === 'TIE') return null;

  const showCounting = elapsed < COUNTING_BEAT_MS;
  const showReveal = elapsed >= revealAt;

  // Bewusst ohne Auszählung: der geschützte Spieler ist der Führende, sein Balken
  // würde ihn namentlich verraten — „Nur Effekt, keine Namen".
  if (result.type === 'NO_ELIMINATION') {
    return (
      <AppShell header={<GameHeader view={view} />}>
        <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center gap-6 text-center">
          {showCounting ? (
            <p className="font-display text-xl text-fg-muted">{de.roundResult.counting}</p>
          ) : (
            <div className="flex animate-phase-in flex-col items-center gap-3">
              <span className="text-5xl" aria-hidden="true">
                {de.specialRoles.BLESSING.emoji}
              </span>
              <p className="font-display text-2xl font-extrabold text-accent">
                {de.roundResult.noElimination}
              </p>
              <p className="text-fg-muted">{de.roundResult.protectedHint}</p>
            </div>
          )}
        </div>
      </AppShell>
    );
  }

  const victim = view.players.find((player) => player.id === result.playerId);
  const maxCount = entry ? Math.max(...entry.tally.map((row) => row.count), 1) : result.voteCount;
  const rows = entry
    ? [...entry.tally].sort((a, b) => b.count - a.count)
    : [{ playerId: result.playerId, count: result.voteCount }];

  return (
    <AppShell header={<GameHeader view={view} />}>
      <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center gap-6 text-center">
        {showCounting ? (
          <p className="font-display text-xl text-fg-muted">{de.roundResult.counting}</p>
        ) : (
          <div className="flex w-full animate-phase-in flex-col items-center gap-6">
            <div className="w-full max-w-xs">
              {rows.map((row, index) => (
                <div
                  key={row.playerId}
                  className="animate-phase-in py-1"
                  style={{
                    animationDelay: `${(index * BAR_STAGGER_MS) / 1000}s`,
                    animationFillMode: 'backwards',
                  }}
                >
                  <TallyBar
                    name={view.players.find((p) => p.id === row.playerId)?.name ?? row.playerId}
                    count={row.count}
                    maxCount={maxCount}
                    // Die Hervorhebung kommt erst, wenn alle Balken stehen —
                    // sonst wäre das Ergebnis schon am ersten Balken ablesbar.
                    highlighted={row.playerId === result.playerId && elapsed >= highlightAt}
                    votesLabel={de.roundResult.votesLabel(row.count)}
                  />
                </div>
              ))}
            </div>

            {/* Dauerhaft im Layout, nur unsichtbar: bekäme die Auflösung erst
                beim Erscheinen Platz, sprängen die Balken im zentrierten Layout
                genau im dramatischsten Moment nach oben.

                `visibility` statt einer Opacity-Transition — die schaltet ohne
                Zeitverlauf um. Eine Transition wäre der dritte Weg, Inhalt an
                eine laufende Animation zu koppeln; die Einblendung darüber ist
                Schmuck und darf ausfallen. */}
            <div className={showReveal ? 'animate-phase-in' : 'invisible'}>
              <p className="font-display text-2xl font-extrabold">
                {de.roundResult.eliminated(victim?.name ?? '')}
              </p>
              <p
                className={`mt-1 font-display text-lg font-bold ${
                  factionOf(result.role) === 'MAFIA' ? 'text-mafia' : 'text-citizen'
                }`}
              >
                {de.roundResult.wasRole(de.roles[result.role])}
              </p>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
